import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { Button, Spinner, Alert } from '../components/common/UIComponents';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { resolveDefaultAppLanding, resolvePostAuthRedirect } from '../utils/authRedirect';
import { authService, VerificationEmailResult } from '../services/authService';
import { syncEmailVerificationStatus } from '../services/emailVerification';
import {
  consumePostAuthIntent,
  decodePostAuthIntentFromQuery,
  getPostAuthIntent,
  savePostAuthIntentFromQueryValue,
} from '../utils/postAuthIntent';

type VerificationBannerState = 'success' | 'error' | 'unknown';
const RESEND_COOLDOWN_SECONDS = 45;

const VerifyEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, claims } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [isEscaping, setIsEscaping] = useState(false);
  const [escapeError, setEscapeError] = useState<string | null>(null);
  const verifiedReadbackAttemptedRef = useRef(false);

  const initialVerificationEmail = (location.state as any)?.initialVerificationEmail as
    | VerificationEmailResult
    | undefined;
  const registeredRole = String((location.state as any)?.registeredRole || '').trim();
  const from = (location.state as any)?.from;
  const queryParams = new URLSearchParams(location.search);
  const queryIntentParam = queryParams.get('postAuthIntent');
  const fallback = resolveDefaultAppLanding(userProfile, claims, registeredRole);

  useEffect(() => {
    if (!queryIntentParam) {
      return;
    }
    savePostAuthIntentFromQueryValue(queryIntentParam, 'verify-email-query');
  }, [queryIntentParam]);

  const resolveAuthCompletionPath = useCallback(
    (consumeIntent: boolean): string => {
      const persistedIntentPath = consumeIntent ? consumePostAuthIntent() : getPostAuthIntent();
      const queryIntentPath = decodePostAuthIntentFromQuery(queryIntentParam);
      const intentPath = persistedIntentPath || queryIntentPath;
      return resolvePostAuthRedirect({
        from,
        intentPath,
        fallback,
      });
    },
    [fallback, from, queryIntentParam]
  );

  const initialBannerState: VerificationBannerState =
    initialVerificationEmail?.success === true
      ? 'success'
      : initialVerificationEmail?.success === false
        ? 'error'
        : 'unknown';

  React.useEffect(() => {
    if (initialBannerState === 'success') {
      setResendCooldownSeconds((current) => (current > 0 ? current : RESEND_COOLDOWN_SECONDS));
    }
  }, [initialBannerState]);

  React.useEffect(() => {
    if (resendCooldownSeconds <= 0) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setResendCooldownSeconds((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [resendCooldownSeconds]);

  const handleCheckVerification = useCallback(async () => {
    if (!auth.currentUser) {
      console.error('handleCheckVerification called when auth.currentUser is null');
      setCheckError('Błąd: Użytkownik nie jest zalogowany.');
      return;
    }

    setIsChecking(true);
    setCheckError(null);
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        verifiedReadbackAttemptedRef.current = true;
        try {
          const syncResult = await syncEmailVerificationStatus();
          if (syncResult.success !== true || syncResult.profileStatus !== 'active') {
            throw new Error('Profile verification readback was not confirmed.');
          }
        } catch (_syncError) {
          setCheckError(
            'Adres e-mail jest potwierdzony, ale nie udało się jeszcze potwierdzić statusu konta w aplikacji. Spróbuj ponownie.'
          );
          return;
        }
        const targetPath = resolveAuthCompletionPath(true);
        navigate(targetPath, { replace: true });
      } else {
        setCheckError('Email jeszcze niezweryfikowany. Spróbuj ponownie za chwilę.');
      }
    } catch (error) {
      console.error('Błąd podczas sprawdzania weryfikacji email:', error);
      setCheckError('Wystąpił błąd podczas sprawdzania statusu weryfikacji.');
    } finally {
      setIsChecking(false);
    }
  }, [navigate, resolveAuthCompletionPath]);

  useEffect(() => {
    if (!currentUser?.emailVerified || verifiedReadbackAttemptedRef.current) {
      return;
    }
    verifiedReadbackAttemptedRef.current = true;
    void handleCheckVerification();
  }, [currentUser?.emailVerified, handleCheckVerification]);

  const handleResendVerification = useCallback(async () => {
    if (!auth.currentUser) {
      setResendStatus({ type: 'error', message: 'Błąd: Użytkownik nie jest zalogowany.' });
      return;
    }
    if (resendCooldownSeconds > 0) {
      setResendStatus({
        type: 'error',
        message: `Kolejne żądanie możesz wysłać za ${resendCooldownSeconds} s.`,
      });
      return;
    }
    if (auth.currentUser.emailVerified) {
      setResendStatus({ type: 'error', message: 'Twój email jest już zweryfikowany.' });
      return;
    }

    setIsResending(true);
    setResendStatus(null);
    setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    try {
      const result = await authService.sendVerificationEmail(auth.currentUser, {
        source: 'resend',
      });
      if (!result.success) {
        setResendStatus({
          type: 'error',
          message: result.userMessage || 'Nie udało się wysłać emaila. Spróbuj ponownie.',
        });
        return;
      }
      setResendStatus({
        type: 'success',
        message:
          'Żądanie wysyłki linku weryfikacyjnego zostało przyjęte. Sprawdź skrzynkę odbiorczą i spam.',
      });
    } catch (error: any) {
      console.error('Błąd podczas ponownego wysyłania emaila weryfikacyjnego:', error);
      setResendStatus({ type: 'error', message: 'Nie udało się wysłać emaila. Spróbuj ponownie.' });
    } finally {
      setIsResending(false);
    }
  }, [resendCooldownSeconds]);

  const handleBackToLogin = useCallback(() => {
    setEscapeError(null);
    const loginTarget = queryIntentParam ? `/login?postAuthIntent=${queryIntentParam}` : '/login';
    navigate(loginTarget);
  }, [navigate, queryIntentParam]);

  const handleReturnHome = useCallback(() => {
    setEscapeError(null);
    navigate('/');
  }, [navigate]);

  const handleSignOutAndGoToLogin = useCallback(async () => {
    setEscapeError(null);
    setIsEscaping(true);
    try {
      await authService.logout();
      const loginTarget = queryIntentParam ? `/login?postAuthIntent=${queryIntentParam}` : '/login';
      navigate(loginTarget, { replace: true });
    } catch (error) {
      console.error('Błąd podczas wylogowania z ekranu weryfikacji emaila:', error);
      setEscapeError('Nie udało się wylogować. Spróbuj ponownie.');
    } finally {
      setIsEscaping(false);
    }
  }, [navigate, queryIntentParam]);

  const email = location.state?.email;
  const displayEmail = email || currentUser?.email;

  if (currentUser?.emailVerified) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-primary-dark-100 p-6 text-primary-light-100">
        <div className="max-w-lg rounded-lg border border-primary-dark-300/38 bg-primary-dark-200/58 p-6 text-center">
          <h1 className="text-xl font-bold text-primary-light-100">Sprawdzamy status konta</h1>
          {checkError ? (
            <>
              <div role="alert" className="mt-4 text-left">
                <Alert variant="warning" icon={<ExclamationCircleIcon className="h-5 w-5" />}>
                  {checkError}
                </Alert>
              </div>
              <Button
                onClick={handleCheckVerification}
                disabled={isChecking}
                variant="primary"
                className="mt-5 w-full"
              >
                {isChecking ? 'Sprawdzanie...' : 'Sprawdź status ponownie'}
              </Button>
            </>
          ) : (
            <div className="mt-4" aria-live="polite">
              <Spinner size="md" color="white" className="mx-auto mb-4" />
              <p className="text-primary-light-200">
                Potwierdzamy status profilu przed przejściem dalej...
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!currentUser || !displayEmail) {
    console.warn('VerifyEmailPage: No user or display email found, redirecting to login.');
    const loginPath = queryIntentParam ? `/login?postAuthIntent=${queryIntentParam}` : '/login';
    return <Navigate to={loginPath} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-primary-dark-100 p-4 text-primary-light-100 sm:p-6">
      <div className="max-w-lg animate-fade-in rounded-lg border border-primary-dark-300/38 bg-primary-dark-200/58 p-6 text-center sm:p-8">
        <CheckCircleIcon className="mx-auto mb-5 h-14 w-14 text-ratuj-300" />
        <h1 className="mb-3 text-2xl font-bold text-primary-light-100">
          Dziękujemy za rejestrację!
        </h1>
        <p className="mb-5 text-base text-primary-light-300">
          Aby aktywować konto, potrzebujesz linku weryfikacyjnego na adres:
        </p>
        <p className="mb-7 break-all text-lg font-semibold text-ratuj-200">
          <EnvelopeIcon className="w-5 h-5 inline-block mr-2 align-text-bottom" />
          {displayEmail}
        </p>

        {initialBannerState === 'error' ? (
          <Alert
            variant="warning"
            icon={<ExclamationCircleIcon className="w-5 h-5" />}
            className="text-left"
          >
            Podczas rejestracji nie udało się wysłać pierwszego maila weryfikacyjnego.{' '}
            {initialVerificationEmail?.userMessage ||
              'Użyj przycisku poniżej, aby wysłać link ponownie.'}
          </Alert>
        ) : initialBannerState === 'success' ? (
          <p className="text-md text-primary-light-400">
            Pierwsze żądanie wysyłki linku weryfikacyjnego zostało przyjęte. Sprawdź skrzynkę
            odbiorczą (oraz spam) i kliknij link, aby dokończyć rejestrację.
          </p>
        ) : (
          <p className="text-md text-primary-light-400">
            Nie mamy potwierdzenia wysyłki linku z tej sesji. Użyj przycisku „Wyślij link ponownie”,
            aby wysłać nowy link weryfikacyjny.
          </p>
        )}

        {resendStatus && (
          <Alert
            variant={resendStatus.type === 'success' ? 'success' : 'error'}
            icon={
              resendStatus.type === 'success' ? (
                <CheckCircleIcon className="w-5 h-5" />
              ) : (
                <ExclamationCircleIcon className="w-5 h-5" />
              )
            }
            className="mt-6 text-left"
          >
            {resendStatus.message}
          </Alert>
        )}

        <div className="mt-8">
          {checkError && (
            <Alert
              variant="warning"
              icon={<ExclamationCircleIcon className="w-5 h-5" />}
              className="mb-4 text-left"
            >
              {checkError}
            </Alert>
          )}
          <Button
            onClick={handleCheckVerification}
            disabled={isChecking}
            variant="primary"
            className="w-full md:w-auto"
          >
            {isChecking ? (
              <>
                <Spinner size="sm" color="white" className="mr-2" />
                Sprawdzanie...
              </>
            ) : (
              'Sprawdź status weryfikacji i przejdź do aplikacji'
            )}
          </Button>
        </div>

        <div className="mt-4 border-t border-primary-dark-500 pt-4">
          <Button
            onClick={handleResendVerification}
            disabled={isResending || isChecking || resendCooldownSeconds > 0}
            variant="secondary"
            className="w-full md:w-auto text-sm"
          >
            {isResending ? (
              <>
                <Spinner size="sm" color="primary" className="mr-2" />
                Wysyłanie...
              </>
            ) : resendCooldownSeconds > 0 ? (
              `Wyślij ponownie za ${resendCooldownSeconds}s`
            ) : (
              'Wyślij link ponownie'
            )}
          </Button>
          <p className="text-xs text-primary-light-400 mt-3">
            {resendCooldownSeconds > 0
              ? `Odczekaj ${resendCooldownSeconds}s przed kolejną próbą, aby zmniejszyć ryzyko limitu wysyłki.`
              : 'Jeśli ponowna wysyłka jest chwilowo ograniczona limitem, możesz od razu użyć akcji wyjścia poniżej.'}
          </p>
        </div>

        <div className="mt-6 border-t border-primary-dark-500 pt-4 text-left">
          <h2 className="text-sm font-semibold text-primary-light-200 mb-3">Masz drogę wyjścia</h2>
          {escapeError && (
            <Alert
              variant="error"
              icon={<ExclamationCircleIcon className="w-5 h-5" />}
              className="mb-3"
            >
              {escapeError}
            </Alert>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={handleBackToLogin}
              variant="secondary"
              className="w-full"
              disabled={isEscaping}
            >
              Wróć do logowania
            </Button>
            <Button
              onClick={handleSignOutAndGoToLogin}
              variant="secondary"
              className="w-full"
              disabled={isEscaping}
            >
              {isEscaping ? (
                <>
                  <Spinner size="sm" color="primary" className="mr-2" />
                  Zamykanie...
                </>
              ) : (
                'Zaloguj się innym kontem'
              )}
            </Button>
            <Button
              onClick={handleReturnHome}
              variant="neutral"
              className="w-full sm:col-span-2"
              disabled={isEscaping}
            >
              Wróć do strony głównej
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
